import { useState } from 'react';
import { useScrollAnimation, useStaggerAnimation } from '@/hooks/useScrollAnimation';

const CATEGORIES = ['全部', '科学家', '企业家', '学者', '工程师'];

const PIONEERS = [
  {
    id: 'zuckerberg',
    name: 'Mark Zuckerberg',
    nameCn: '马克·扎克伯格',
    role: '企业家',
    title: 'Meta CEO · 元宇宙布道者',
    avatar: 'https://public.readdy.ai/ai/img_res/99eb724c5a0aa01baa62e3bdf1bfab4c.jpg',
    color: '#00FF88',
    emperor: '汉武帝刘彻',
    emperorDesc: '如汉武帝开疆拓土、建立丝绸之路，扎克伯格以Facebook帝国为基，豪掷千亿美元押注元宇宙，将数字疆域延伸至人类意识的边界。汉武帝征匈奴，他征虚空；汉武帝建长城，他建数字围墙。两者皆是时代的强力推进者，也皆承受着巨大的争议与代价。',
    events: [
      { year: '2004', event: '创立Facebook，开启社交网络时代' },
      { year: '2012', event: '以10亿美元收购Instagram，构建社交帝国' },
      { year: '2014', event: '以20亿美元收购Oculus，布局VR赛道' },
      { year: '2021', event: '将Facebook更名为Meta，全面押注元宇宙' },
      { year: '2023', event: '发布Quest 3，推出Llama大模型' },
      { year: '2024', event: 'Meta AI全面整合，元宇宙战略深化' },
    ],
    contribution: '将元宇宙从科幻概念推向全球主流视野，投入超过500亿美元构建元宇宙基础设施，推动XR设备普及化。',
    quote: '元宇宙将是下一个重大计算平台，就像移动互联网取代桌面互联网一样。',
    tags: ['元宇宙', 'VR/AR', '社交网络', 'AI大模型'],
  },
  {
    id: 'musk',
    name: 'Elon Musk',
    nameCn: '埃隆·马斯克',
    role: '企业家',
    title: 'Tesla/SpaceX/Neuralink CEO',
    avatar: 'https://readdy.ai/api/search-image?query=Elon%20Musk%20portrait%20professional%20photo%20realistic%20face%20man%20dark%20background%20studio%20lighting%20confident%20expression&width=200&height=200&seq=pioneer-musk-001&orientation=squarish',
    color: '#FFD700',
    emperor: '秦始皇嬴政',
    emperorDesc: '如秦始皇统一六国、书同文车同轨，马斯克以一己之力重塑电动车、航天、脑机接口、AI多个产业。秦始皇修长城防外患，他建Neuralink连接人脑；秦始皇统一度量衡，他以X平台统一信息流。两者皆是颠覆性的时代重塑者，以极端的执行力和争议性的手段改变世界格局。',
    events: [
      { year: '2002', event: '创立SpaceX，立志将人类送上火星' },
      { year: '2003', event: '联合创立Tesla，推动电动车革命' },
      { year: '2016', event: '创立Neuralink，研发脑机接口技术' },
      { year: '2022', event: '收购Twitter（现X），重塑信息生态' },
      { year: '2023', event: '创立xAI，发布Grok大模型' },
      { year: '2024', event: 'Optimus人形机器人量产，Neuralink首次人体植入' },
    ],
    contribution: '通过Neuralink推动脑机接口技术商业化，以SpaceX推进人类多星球文明，为元宇宙提供终极硬件接口与星际基础设施。',
    quote: '如果你不努力工作，你就无法改变世界。我们必须成为多星球物种。',
    tags: ['脑机接口', '火星移民', '人形机器人', 'AI'],
  },
  {
    id: 'stephenson',
    name: 'Neal Stephenson',
    nameCn: '尼尔·斯蒂芬森',
    role: '学者',
    title: '科幻作家 · 元宇宙概念之父',
    avatar: 'https://readdy.ai/api/search-image?query=Neal%20Stephenson%20author%20portrait%20professional%20photo%20realistic%20face%20older%20man%20glasses%20dark%20background%20studio%20lighting&width=200&height=200&seq=pioneer-stephenson-001&orientation=squarish',
    color: '#A855F7',
    emperor: '老子李耳',
    emperorDesc: '如老子著《道德经》五千言，以哲学之笔描绘宇宙本质，斯蒂芬森以《雪崩》一书预言了整个数字文明的走向。老子的"道"是万物运行的底层逻辑，斯蒂芬森的"元宇宙"是数字世界的底层架构。两者皆是以思想之力改变世界的先知，其影响力在身后数十年乃至数百年后才被世人充分认识。',
    events: [
      { year: '1992', event: '出版《雪崩》，首次提出"元宇宙"概念' },
      { year: '1999', event: '出版《密码宝典》，预言密码学与数字货币' },
      { year: '2011', event: '出版《Reamde》，探索虚拟世界经济体系' },
      { year: '2019', event: '出版《Fall》，深度探讨数字意识上传' },
      { year: '2022', event: '加入Lamina1区块链项目，构建开放元宇宙' },
    ],
    contribution: '1992年在《雪崩》中创造"Metaverse"一词，为整个元宇宙产业提供了概念框架和哲学基础，影响了扎克伯格、任宇昕等一代科技领袖。',
    quote: '元宇宙是一个平行于现实世界的虚拟空间，人们以数字化身在其中生活、工作和娱乐。',
    tags: ['元宇宙概念', '科幻文学', '数字哲学', '密码学'],
  },
  {
    id: 'lecun',
    name: 'Yann LeCun',
    nameCn: '杨立昆',
    role: '科学家',
    title: 'Meta首席AI科学家 · 深度学习之父',
    avatar: 'https://readdy.ai/api/search-image?query=Yann%20LeCun%20AI%20scientist%20portrait%20professional%20photo%20realistic%20face%20older%20man%20dark%20background%20studio%20lighting&width=200&height=200&seq=pioneer-lecun-001&orientation=squarish',
    color: '#00FFCC',
    emperor: '张衡',
    emperorDesc: '如东汉科学家张衡发明地动仪、浑天仪，以超越时代的科学洞察力改变人类认知，杨立昆发明卷积神经网络，奠定了现代AI的数学基础。张衡的地动仪在当时无人能理解其价值，杨立昆的CNN在1990年代同样被主流学界忽视，直到深度学习革命爆发才被世人认可。两者皆是孤独的先行者，以数十年的坚持等来了时代的回响。',
    events: [
      { year: '1989', event: '发明卷积神经网络(CNN)，奠定深度学习基础' },
      { year: '2003', event: '加入纽约大学，建立深度学习研究中心' },
      { year: '2013', event: '加入Facebook，创立AI研究院FAIR' },
      { year: '2018', event: '与Hinton、Bengio共同获得图灵奖' },
      { year: '2022', event: '提出"世界模型"理论，推动AGI研究' },
    ],
    contribution: '卷积神经网络是现代AI视觉识别的基础，直接支撑了元宇宙中的空间感知、手势识别、虚拟形象生成等核心技术。',
    quote: '真正的智能需要理解世界的运作方式，而不仅仅是识别模式。',
    tags: ['深度学习', 'CNN', 'AI视觉', '图灵奖'],
  },
  {
    id: 'hinton',
    name: 'Geoffrey Hinton',
    nameCn: '杰弗里·辛顿',
    role: '科学家',
    title: 'AI教父 · 神经网络先驱',
    avatar: 'https://readdy.ai/api/search-image?query=Geoffrey%20Hinton%20AI%20scientist%20portrait%20professional%20photo%20realistic%20face%20elderly%20man%20dark%20background%20studio%20lighting&width=200&height=200&seq=pioneer-hinton-001&orientation=squarish',
    color: '#FF6B9D',
    emperor: '孔子',
    emperorDesc: '如孔子周游列国传播儒学，培育了无数弟子改变中国文明走向，辛顿以多伦多大学为根据地，培养了LeCun、Bengio等一代AI领袖，其思想通过弟子们渗透到全球每一个AI实验室。孔子晚年整理六经，辛顿晚年离开谷歌警示AI风险，两者皆是以教育和思想影响力超越个人成就的精神领袖。',
    events: [
      { year: '1986', event: '提出反向传播算法，奠定神经网络训练基础' },
      { year: '2012', event: 'AlexNet赢得ImageNet竞赛，引爆深度学习革命' },
      { year: '2013', event: '加入Google Brain，推动AI产业化' },
      { year: '2018', event: '获图灵奖，被誉为"AI教父"' },
      { year: '2023', event: '离开Google，公开警示AI存在性风险' },
    ],
    contribution: '反向传播算法是训练深度神经网络的核心方法，是元宇宙中所有AI生成内容、智能NPC、实时渲染优化的算法基础。',
    quote: '我们正在创造比人类更聪明的东西，这可能是人类历史上最重要的事情，也可能是最后一件事。',
    tags: ['神经网络', '反向传播', 'AI安全', '图灵奖'],
  },
  {
    id: 'nakamoto',
    name: 'Satoshi Nakamoto',
    nameCn: '中本聪',
    role: '工程师',
    title: '比特币创始人 · 区块链之父',
    avatar: 'https://readdy.ai/api/search-image?query=mysterious%20anonymous%20person%20silhouette%20portrait%20dark%20background%20glowing%20digital%20code%20bitcoin%20symbol&width=200&height=200&seq=pioneer-nakamoto-001&orientation=squarish',
    color: '#FF6B35',
    emperor: '商鞅',
    emperorDesc: '如商鞅变法以法制重构秦国经济与社会秩序，中本聪以比特币白皮书重构了人类的价值交换体系。商鞅以"法"取代"礼"，中本聪以"代码"取代"信任"；商鞅的变法在其死后仍深刻影响中国两千年，中本聪的区块链在其消失后仍以万亿美元的市值证明其思想的生命力。两者皆是以制度设计改变文明走向的神秘改革者。',
    events: [
      { year: '2008', event: '发布比特币白皮书，提出区块链概念' },
      { year: '2009', event: '挖出比特币创世区块，区块链时代开启' },
      { year: '2010', event: '将比特币项目移交社区，神秘消失' },
      { year: '2021', event: '比特币市值突破1万亿美元' },
      { year: '2024', event: '比特币ETF获批，机构资金大规模入场' },
    ],
    contribution: '区块链技术是元宇宙数字资产确权、NFT、去中心化经济体系的底层基础设施，中本聪的发明直接催生了价值数十万亿美元的数字经济生态。',
    quote: '如果你不理解或不相信它，我没有时间说服你，对不起。',
    tags: ['区块链', '比特币', '去中心化', '数字货币'],
  },
  {
    id: 'berners-lee',
    name: 'Tim Berners-Lee',
    nameCn: '蒂姆·伯纳斯-李',
    role: '工程师',
    title: '万维网发明者 · Web之父',
    avatar: 'https://readdy.ai/api/search-image?query=Tim%20Berners-Lee%20web%20inventor%20portrait%20professional%20photo%20realistic%20face%20older%20man%20dark%20background%20studio%20lighting&width=200&height=200&seq=pioneer-tbl-001&orientation=squarish',
    color: '#00FF88',
    emperor: '蔡伦',
    emperorDesc: '如东汉蔡伦改进造纸术，将知识的载体从竹简变为纸张，彻底改变了人类文明的传播方式，伯纳斯-李发明万维网，将信息的载体从纸张变为超链接，开启了人类历史上最大规模的知识民主化运动。蔡伦的纸张让书籍走进千家万户，伯纳斯-李的WWW让知识触手可及。两者皆是以技术工具改变文明传播方式的伟大工匠。',
    events: [
      { year: '1989', event: '在CERN提出万维网概念' },
      { year: '1991', event: '发布第一个网站，互联网时代开启' },
      { year: '1994', event: '创立W3C，推动Web标准化' },
      { year: '2009', event: '创立万维网基金会，推动数字权利' },
      { year: '2021', event: '以NFT出售WWW源代码，探索Web3.0' },
    ],
    contribution: '万维网是元宇宙的前身，其超链接、HTTP协议、HTML标准构成了数字世界的基础语言，Web5.0的去中心化愿景正是对其初心的回归。',
    quote: '网络的力量在于其普遍性，无论残疾与否，每个人都能访问是其核心方面。',
    tags: ['万维网', 'Web标准', '数字权利', 'Web3.0'],
  },
  {
    id: 'turing',
    name: 'Alan Turing',
    nameCn: '艾伦·图灵',
    role: '科学家',
    title: '计算机科学之父 · AI先知',
    avatar: 'https://readdy.ai/api/search-image?query=Alan%20Turing%20historical%20portrait%20professional%20photo%20realistic%20face%20young%20man%201940s%20style%20dark%20background%20studio%20lighting&width=200&height=200&seq=pioneer-turing-001&orientation=squarish',
    color: '#FFD700',
    emperor: '诸葛亮',
    emperorDesc: '如诸葛亮以超凡智慧辅佐蜀汉，发明木牛流马、诸葛连弩等超越时代的机械，图灵以数学天才破解恩尼格玛密码、提出图灵机理论，奠定了整个计算机科学的理论基础。诸葛亮鞠躬尽瘁死而后已，图灵在最辉煌的年华被迫害致死，两者皆是以天才之躯承受时代不公的悲剧英雄，其思想遗产却照耀了后世数百年。',
    events: [
      { year: '1936', event: '提出图灵机理论，奠定计算机科学基础' },
      { year: '1939', event: '破解纳粹恩尼格玛密码，改变二战走向' },
      { year: '1950', event: '提出图灵测试，开创人工智能研究' },
      { year: '1952', event: '因同性恋被英国政府迫害，接受化学阉割' },
      { year: '1954', event: '英年早逝，年仅41岁' },
    ],
    contribution: '图灵机是所有计算机的理论原型，图灵测试是AI研究的哲学基础，没有图灵，就没有计算机，没有互联网，没有元宇宙。',
    quote: '我们只能看到前方很短的距离，但我们可以看到那里有很多需要做的事情。',
    tags: ['计算机理论', '人工智能', '密码学', '图灵测试'],
  },
  {
    id: 'altman',
    name: 'Sam Altman',
    nameCn: '山姆·奥特曼',
    role: '企业家',
    title: 'OpenAI CEO · AGI推进者',
    avatar: 'https://readdy.ai/api/search-image?query=Sam%20Altman%20OpenAI%20CEO%20portrait%20professional%20photo%20realistic%20face%20young%20man%20dark%20background%20studio%20lighting&width=200&height=200&seq=pioneer-altman-001&orientation=squarish',
    color: '#00FFCC',
    emperor: '朱元璋',
    emperorDesc: '如朱元璋从一介布衣起家，以惊人的战略眼光和执行力建立大明王朝，奥特曼从YC孵化器出发，以ChatGPT一夜之间改变了全球AI格局。朱元璋建立了中国历史上最严密的中央集权体系，奥特曼构建了AI时代最具影响力的技术帝国。两者皆是以草根出身逆袭成为时代主宰的传奇人物，其崛起速度之快令所有人瞠目结舌。',
    events: [
      { year: '2014', event: '成为Y Combinator总裁，孵化数百家独角兽' },
      { year: '2019', event: '出任OpenAI CEO，获微软10亿美元投资' },
      { year: '2022', event: '发布ChatGPT，引爆全球AI革命' },
      { year: '2023', event: '发布GPT-4，被董事会短暂解雇后复职' },
      { year: '2024', event: 'OpenAI估值突破1500亿美元，推进AGI研究' },
    ],
    contribution: 'ChatGPT和GPT系列模型是元宇宙中AI NPC、智能助手、内容生成的核心引擎，直接推动了AIGC在元宇宙中的大规模应用。',
    quote: '我认为AGI将是人类历史上最具变革性的技术，它将解决我们面临的许多最大问题。',
    tags: ['ChatGPT', 'AGI', 'AIGC', 'OpenAI'],
  },
  {
    id: 'huang',
    name: 'Jensen Huang',
    nameCn: '黄仁勋',
    role: '企业家',
    title: 'NVIDIA CEO · 元宇宙算力之王',
    avatar: 'https://readdy.ai/api/search-image?query=Jensen%20Huang%20NVIDIA%20CEO%20portrait%20professional%20photo%20realistic%20face%20asian%20man%20dark%20background%20studio%20lighting%20leather%20jacket&width=200&height=200&seq=pioneer-huang-001&orientation=squarish',
    color: '#FF6B35',
    emperor: '李冰',
    emperorDesc: '如战国时期李冰父子修建都江堰，以水利工程滋养天府之国两千年，黄仁勋以GPU算力工程滋养了整个AI和元宇宙产业。都江堰是农业文明的基础设施，NVIDIA GPU是数字文明的基础设施；李冰的工程让成都平原成为天府之国，黄仁勋的芯片让硅谷成为数字文明的发源地。两者皆是以工程师精神构建时代基础设施的伟大建设者。',
    events: [
      { year: '1993', event: '联合创立NVIDIA，专注GPU研发' },
      { year: '2006', event: '发布CUDA平台，开启GPU通用计算时代' },
      { year: '2016', event: '发布DGX-1，推动深度学习算力革命' },
      { year: '2021', event: '发布Omniverse平台，构建元宇宙基础设施' },
      { year: '2024', event: 'NVIDIA市值突破3万亿美元，成为全球最有价值公司' },
    ],
    contribution: 'NVIDIA GPU是训练AI模型、渲染元宇宙场景、运行数字孪生的核心算力基础，Omniverse平台是工业元宇宙的标准基础设施。',
    quote: '元宇宙将是下一个互联网，而NVIDIA将是其中的基础设施提供商。',
    tags: ['GPU算力', 'Omniverse', '数字孪生', 'AI芯片'],
  },
  {
    id: 'lanier',
    name: 'Jaron Lanier',
    nameCn: '贾伦·拉尼尔',
    role: '科学家',
    title: 'VR之父 · 数字人文主义者',
    avatar: 'https://readdy.ai/api/search-image?query=Jaron%20Lanier%20VR%20pioneer%20portrait%20professional%20photo%20realistic%20face%20man%20dreadlocks%20dark%20background%20studio%20lighting&width=200&height=200&seq=pioneer-lanier-001&orientation=squarish',
    color: '#A855F7',
    emperor: '王阳明',
    emperorDesc: '如明代哲学家王阳明提出"知行合一"，以人文主义精神对抗程朱理学的僵化教条，拉尼尔以"数字人文主义"对抗硅谷的技术至上主义。王阳明在龙场悟道，拉尼尔在VR实验室中顿悟技术的人文本质；王阳明的心学强调人的主体性，拉尼尔的思想强调技术应服务于人的尊严。两者皆是在技术与人文之间寻找平衡的思想先驱。',
    events: [
      { year: '1984', event: '创立VPL Research，发明第一款商业VR头显' },
      { year: '1987', event: '发明数据手套，开创触觉交互技术' },
      { year: '2010', event: '出版《你不是一个小工具》，批判数字文化' },
      { year: '2013', event: '加入微软研究院，研究混合现实' },
      { year: '2018', event: '出版《删除你的社交媒体账号》，警示数字异化' },
    ],
    contribution: '发明了VR头显、数据手套等核心XR硬件，是元宇宙沉浸式体验的技术奠基人，同时以人文主义视角为元宇宙发展提供伦理框架。',
    quote: '虚拟现实是一种新的艺术形式，它让我们能够体验彼此的内心世界。',
    tags: ['VR发明', '数据手套', '数字人文', 'XR先驱'],
  },
  {
    id: 'ma',
    name: 'Jack Ma',
    nameCn: '马云',
    role: '企业家',
    title: '阿里巴巴创始人 · 数字经济先驱',
    avatar: 'https://readdy.ai/api/search-image?query=Jack%20Ma%20Alibaba%20founder%20portrait%20professional%20photo%20realistic%20face%20asian%20man%20dark%20background%20studio%20lighting&width=200&height=200&seq=pioneer-ma-001&orientation=squarish',
    color: '#FFD700',
    emperor: '范蠡',
    emperorDesc: '如春秋时期范蠡辅佐越王勾践复国后急流勇退，转而经商成为"陶朱公"，以商业智慧三聚三散财富，马云以阿里巴巴构建数字商业帝国后主动退休，转而关注教育与公益。范蠡深谙"功成身退"之道，马云亦在巅峰时刻选择离开；范蠡的商业哲学影响中国两千年，马云的电商模式改变了全球数十亿人的购物方式。',
    events: [
      { year: '1999', event: '创立阿里巴巴，开创B2B电商模式' },
      { year: '2003', event: '创立淘宝，颠覆中国零售业' },
      { year: '2004', event: '创立支付宝，开创移动支付时代' },
      { year: '2014', event: '阿里巴巴纽交所上市，创历史最大IPO' },
      { year: '2019', event: '卸任阿里巴巴董事局主席，转型教育公益' },
    ],
    contribution: '支付宝和阿里云是元宇宙数字经济的重要基础设施，阿里的数字商业模式为元宇宙虚拟经济体系提供了成熟的商业范本。',
    quote: '今天很残酷，明天更残酷，后天很美好，但大多数人死在明天晚上。',
    tags: ['数字经济', '电商革命', '移动支付', '云计算'],
  },
];

export default function MetaPioneers() {
  const [activeCategory, setActiveCategory] = useState('全部');
  const [selectedPioneer, setSelectedPioneer] = useState<typeof PIONEERS[0] | null>(null);
  const [activeTab, setActiveTab] = useState<'events' | 'emperor' | 'contribution'>('events');
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation({ threshold: 0.08 });
  const { ref: gridRef, visibleItems } = useStaggerAnimation(PIONEERS.length, { threshold: 0.04 });

  const filtered = activeCategory === '全部' ? PIONEERS : PIONEERS.filter(p => p.role === activeCategory);

  return (
    <section id="meta-pioneers" className="relative py-24 md:py-32 overflow-hidden" style={{ background: 'linear-gradient(180deg, #030810 0%, #050A14 50%, #030810 100%)' }}>
      {/* Background */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=hall%20of%20fame%20digital%20pioneers%20metaverse%20technology%20leaders%20glowing%20portraits%20holographic%20dark%20background%20futuristic%20museum%20sci-fi%20ultra%20wide%20cinematic%208k&width=1920&height=1080&seq=pioneers-bg-001&orientation=landscape"
          alt=""
          className="w-full h-full object-cover opacity-5"
        />
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 50% 30%, rgba(0,255,136,0.04) 0%, transparent 60%)' }} />
        <div className="absolute inset-0 opacity-8" style={{
          backgroundImage: 'linear-gradient(rgba(255,215,0,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(255,215,0,0.06) 1px, transparent 1px)',
          backgroundSize: '80px 80px',
        }} />
        {/* Decorative SVG */}
        <svg className="absolute top-0 left-0 w-full h-full opacity-10" viewBox="0 0 1440 900" preserveAspectRatio="none">
          <path d="M0,40 Q360,10 720,50 Q1080,90 1440,30" stroke="#FFD700" strokeWidth="2" fill="none" strokeDasharray="15,25" opacity="0.4"/>
          <circle cx="100" cy="200" r="120" stroke="#00FF88" strokeWidth="1.5" fill="none" strokeDasharray="8,16" opacity="0.2"/>
          <circle cx="1340" cy="700" r="150" stroke="#A855F7" strokeWidth="1.5" fill="none" strokeDasharray="8,16" opacity="0.2"/>
          <path d="M720,0 L720,900" stroke="rgba(255,215,0,0.08)" strokeWidth="1" strokeDasharray="20,30"/>
        </svg>
      </div>

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10">
        {/* Header */}
        <div
          ref={headerRef as React.RefObject<HTMLDivElement>}
          className="text-center mb-16"
          style={{
            opacity: headerVisible ? 1 : 0,
            transform: headerVisible ? 'translateY(0)' : 'translateY(50px)',
            transition: 'opacity 0.9s ease, transform 0.9s ease',
          }}
        >
          <div
            className="inline-flex items-center gap-3 px-6 py-2.5 mb-8"
            style={{
              background: 'linear-gradient(135deg, rgba(255,215,0,0.12), rgba(255,107,53,0.12))',
              border: '2px solid rgba(255,215,0,0.4)',
              borderRadius: '50% 45% 55% 50% / 50% 55% 45% 50%',
              color: '#FFD700',
              fontFamily: 'Caveat, cursive',
              fontSize: '1.1rem',
              boxShadow: '0 0 30px rgba(255,215,0,0.15)',
            }}
          >
            <i className="ri-user-star-line" />
            META人物志 · 改变数字文明的先行者
            <i className="ri-user-star-line" />
          </div>

          <h2 className="text-5xl md:text-6xl lg:text-7xl font-black text-white mb-6 leading-tight" style={{ fontFamily: 'Orbitron, sans-serif' }}>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FFD700] via-[#FF6B35] to-[#FF6B9D]">
              数字文明
            </span>
            <span className="block text-3xl md:text-4xl mt-2 text-white/90">先行者名人堂</span>
          </h2>

          <p className="text-white/55 text-lg max-w-4xl mx-auto leading-relaxed mb-8">
            他们是元宇宙时代的奠基者——科学家、企业家、工程师与思想家。
            他们的每一个决策，都在重塑人类文明的走向。
            <span className="text-[#FFD700]">如古代帝王开疆拓土</span>，他们以代码与算法构建数字帝国。
          </p>

          {/* Category Filter */}
          <div className="flex flex-wrap gap-3 justify-center">
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className="flex items-center gap-2 px-5 py-2.5 font-bold transition-all duration-300 cursor-pointer whitespace-nowrap"
                style={{
                  background: activeCategory === cat ? 'rgba(255,215,0,0.15)' : 'rgba(255,255,255,0.04)',
                  border: `2px solid ${activeCategory === cat ? 'rgba(255,215,0,0.6)' : 'rgba(255,255,255,0.12)'}`,
                  borderRadius: '10px 8px 12px 8px / 8px 12px 8px 10px',
                  color: activeCategory === cat ? '#FFD700' : 'rgba(255,255,255,0.45)',
                  transform: activeCategory === cat ? 'scale(1.05)' : 'scale(1)',
                  boxShadow: activeCategory === cat ? '0 0 20px rgba(255,215,0,0.2)' : 'none',
                  fontFamily: 'Caveat, cursive',
                  fontSize: '1rem',
                }}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Pioneers Grid */}
        <div
          ref={gridRef as React.RefObject<HTMLDivElement>}
          className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4 mb-16"
        >
          {filtered.map((pioneer, idx) => {
            const isVis = visibleItems[idx];
            return (
              <div
                key={pioneer.id}
                onClick={() => { setSelectedPioneer(pioneer); setActiveTab('events'); }}
                className="group relative cursor-pointer"
                style={{
                  opacity: isVis ? 1 : 0,
                  transform: isVis ? 'translateY(0) scale(1)' : 'translateY(40px) scale(0.9)',
                  transition: `opacity 0.6s ease ${idx * 80}ms, transform 0.6s cubic-bezier(0.34,1.56,0.64,1) ${idx * 80}ms`,
                }}
              >
                <div
                  className="relative p-4 text-center transition-all duration-300 group-hover:scale-105"
                  style={{
                    background: `linear-gradient(135deg, ${pioneer.color}08, rgba(255,255,255,0.02))`,
                    border: `1.5px solid ${pioneer.color}25`,
                    borderRadius: '16px 12px 18px 14px / 12px 18px 14px 16px',
                  }}
                >
                  {/* Glow on hover */}
                  <div
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                    style={{
                      background: `radial-gradient(ellipse at 50% 30%, ${pioneer.color}12 0%, transparent 70%)`,
                      borderRadius: '16px 12px 18px 14px / 12px 18px 14px 16px',
                    }}
                  />

                  {/* Avatar */}
                  <div
                    className="relative w-16 h-16 mx-auto mb-3 overflow-hidden"
                    style={{
                      border: `2px solid ${pioneer.color}50`,
                      borderRadius: '50% 45% 55% 50% / 50% 55% 45% 50%',
                      boxShadow: `0 0 20px ${pioneer.color}25`,
                    }}
                  >
                    <img
                      src={pioneer.avatar}
                      alt={pioneer.nameCn}
                      className="w-full h-full object-cover object-top"
                    />
                    <div
                      className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                      style={{ background: `linear-gradient(to bottom, transparent 50%, ${pioneer.color}30 100%)` }}
                    />
                  </div>

                  {/* Role badge */}
                  <div
                    className="inline-flex items-center gap-1 px-2 py-0.5 mb-2 text-xs font-bold"
                    style={{
                      background: `${pioneer.color}15`,
                      color: pioneer.color,
                      border: `1px solid ${pioneer.color}35`,
                      borderRadius: '50% 45% 55% 50% / 50% 55% 45% 50%',
                      fontFamily: 'Caveat, cursive',
                      fontSize: '0.75rem',
                    }}
                  >
                    {pioneer.role}
                  </div>

                  <h3 className="text-white font-bold text-sm leading-tight mb-0.5">{pioneer.nameCn}</h3>
                  <p className="text-white/35 text-xs leading-tight">{pioneer.name}</p>

                  {/* Emperor comparison hint */}
                  <div
                    className="mt-2 px-2 py-1 text-xs opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    style={{
                      background: 'rgba(255,215,0,0.08)',
                      border: '1px solid rgba(255,215,0,0.2)',
                      borderRadius: '6px',
                      color: 'rgba(255,215,0,0.7)',
                      fontFamily: 'Caveat, cursive',
                      fontSize: '0.72rem',
                    }}
                  >
                    如{pioneer.emperor}
                  </div>

                  {/* Bottom accent */}
                  <div
                    className="absolute bottom-0 left-4 right-4 h-0.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    style={{ background: `linear-gradient(90deg, transparent, ${pioneer.color}, transparent)` }}
                  />
                </div>
              </div>
            );
          })}
        </div>

        {/* Featured Quote Banner */}
        <div
          className="relative rounded-3xl p-10 mb-16 overflow-hidden text-center"
          style={{
            background: 'linear-gradient(135deg, rgba(255,215,0,0.06), rgba(255,107,53,0.06), rgba(168,85,247,0.06))',
            border: '2px solid rgba(255,215,0,0.2)',
            borderRadius: '28px 20px 32px 24px / 20px 32px 24px 28px',
          }}
        >
          <div className="absolute top-4 left-8 text-6xl opacity-10" style={{ color: '#FFD700', fontFamily: 'Georgia, serif' }}>"</div>
          <div className="absolute bottom-4 right-8 text-6xl opacity-10" style={{ color: '#FFD700', fontFamily: 'Georgia, serif' }}>"</div>
          <p className="text-white/70 text-xl md:text-2xl leading-relaxed max-w-4xl mx-auto mb-4" style={{ fontFamily: 'Caveat, cursive', fontSize: '1.3rem' }}>
            每一个改变世界的人，都曾被世界嘲笑过。<br />
            <span className="text-[#FFD700]">元宇宙的先行者们，正在书写人类文明最精彩的篇章。</span>
          </p>
          <p className="text-white/30 text-sm" style={{ fontFamily: 'Caveat, cursive' }}>— META66 人物志编辑部</p>
        </div>
      </div>

      {/* Pioneer Detail Modal */}
      {selectedPioneer && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.92)', backdropFilter: 'blur(20px)' }}
          onClick={() => setSelectedPioneer(null)}
        >
          <div
            className="relative w-full max-w-3xl max-h-[90vh] overflow-y-auto"
            style={{
              background: 'linear-gradient(135deg, #060C18, #080E1C)',
              border: `2px solid ${selectedPioneer.color}40`,
              borderRadius: '24px 18px 28px 20px / 18px 28px 20px 24px',
              boxShadow: `0 0 80px ${selectedPioneer.color}20`,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div
              className="relative p-8 pb-6"
              style={{
                background: `linear-gradient(135deg, ${selectedPioneer.color}10, transparent)`,
                borderBottom: `1px solid ${selectedPioneer.color}20`,
              }}
            >
              <button
                onClick={() => setSelectedPioneer(null)}
                className="absolute top-5 right-5 w-9 h-9 flex items-center justify-center cursor-pointer transition-all hover:scale-110"
                style={{
                  background: 'rgba(255,255,255,0.08)',
                  border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: '50%',
                  color: 'rgba(255,255,255,0.6)',
                }}
              >
                <i className="ri-close-line text-lg" />
              </button>

              <div className="flex items-start gap-6">
                <div
                  className="w-24 h-24 flex-shrink-0 overflow-hidden"
                  style={{
                    border: `3px solid ${selectedPioneer.color}60`,
                    borderRadius: '50% 45% 55% 50% / 50% 55% 45% 50%',
                    boxShadow: `0 0 30px ${selectedPioneer.color}30`,
                  }}
                >
                  <img src={selectedPioneer.avatar} alt={selectedPioneer.nameCn} className="w-full h-full object-cover object-top" />
                </div>
                <div className="flex-1">
                  <div
                    className="inline-flex items-center gap-2 px-3 py-1 mb-3 text-xs font-bold"
                    style={{
                      background: `${selectedPioneer.color}15`,
                      color: selectedPioneer.color,
                      border: `1px solid ${selectedPioneer.color}40`,
                      borderRadius: '50% 45% 55% 50% / 50% 55% 45% 50%',
                      fontFamily: 'Caveat, cursive',
                    }}
                  >
                    {selectedPioneer.role}
                  </div>
                  <h2 className="text-white font-black text-2xl mb-1" style={{ fontFamily: 'Orbitron, sans-serif' }}>{selectedPioneer.nameCn}</h2>
                  <p className="text-white/50 text-sm mb-2">{selectedPioneer.name}</p>
                  <p style={{ color: selectedPioneer.color }} className="text-sm font-semibold">{selectedPioneer.title}</p>
                  <div className="flex flex-wrap gap-2 mt-3">
                    {selectedPioneer.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-2.5 py-1 text-xs font-semibold"
                        style={{
                          background: `${selectedPioneer.color}10`,
                          color: selectedPioneer.color,
                          border: `1px solid ${selectedPioneer.color}30`,
                          borderRadius: '50% 45% 55% 50% / 50% 55% 45% 50%',
                          fontFamily: 'Caveat, cursive',
                          fontSize: '0.8rem',
                        }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Quote */}
              <div
                className="mt-5 p-4 rounded-xl"
                style={{
                  background: `${selectedPioneer.color}06`,
                  border: `1px solid ${selectedPioneer.color}20`,
                  borderRadius: '12px 8px 14px 10px / 8px 14px 10px 12px',
                }}
              >
                <p className="text-white/65 text-sm leading-relaxed italic" style={{ fontFamily: 'Caveat, cursive', fontSize: '0.95rem' }}>
                  "{selectedPioneer.quote}"
                </p>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 p-4 pb-0">
              {[
                { id: 'events', label: '重要事件', icon: 'ri-time-line' },
                { id: 'emperor', label: '古代帝王类比', icon: 'ri-vip-crown-line' },
                { id: 'contribution', label: 'META贡献', icon: 'ri-rocket-line' },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as typeof activeTab)}
                  className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap"
                  style={{
                    background: activeTab === tab.id ? `${selectedPioneer.color}15` : 'transparent',
                    border: `1.5px solid ${activeTab === tab.id ? selectedPioneer.color + '50' : 'transparent'}`,
                    borderRadius: '10px 8px 12px 8px / 8px 12px 8px 10px',
                    color: activeTab === tab.id ? selectedPioneer.color : 'rgba(255,255,255,0.35)',
                    fontFamily: 'Caveat, cursive',
                    fontSize: '0.9rem',
                  }}
                >
                  <i className={`${tab.icon} text-sm`} />
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <div className="p-6">
              {activeTab === 'events' && (
                <div className="relative">
                  <div className="absolute left-4 top-0 bottom-0 w-px" style={{ background: `linear-gradient(to bottom, ${selectedPioneer.color}60, transparent)` }} />
                  <div className="flex flex-col gap-4 pl-10">
                    {selectedPioneer.events.map((ev, i) => (
                      <div key={i} className="relative">
                        <div
                          className="absolute -left-10 top-1 w-4 h-4 flex items-center justify-center"
                          style={{
                            background: `${selectedPioneer.color}20`,
                            border: `2px solid ${selectedPioneer.color}60`,
                            borderRadius: '50%',
                          }}
                        >
                          <div className="w-1.5 h-1.5 rounded-full" style={{ background: selectedPioneer.color }} />
                        </div>
                        <div
                          className="p-4"
                          style={{
                            background: 'rgba(255,255,255,0.02)',
                            border: '1px solid rgba(255,255,255,0.06)',
                            borderRadius: '10px 8px 12px 8px / 8px 12px 8px 10px',
                          }}
                        >
                          <span className="font-black text-sm mr-3" style={{ color: selectedPioneer.color, fontFamily: 'Orbitron, sans-serif' }}>{ev.year}</span>
                          <span className="text-white/65 text-sm">{ev.event}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {activeTab === 'emperor' && (
                <div>
                  <div
                    className="flex items-center gap-4 p-5 mb-5 rounded-2xl"
                    style={{
                      background: 'linear-gradient(135deg, rgba(255,215,0,0.08), rgba(255,107,53,0.08))',
                      border: '1.5px solid rgba(255,215,0,0.25)',
                      borderRadius: '16px 12px 18px 14px / 12px 18px 14px 16px',
                    }}
                  >
                    <div
                      className="w-16 h-16 flex items-center justify-center flex-shrink-0 text-3xl"
                      style={{
                        background: 'rgba(255,215,0,0.15)',
                        border: '2px solid rgba(255,215,0,0.4)',
                        borderRadius: '50% 40% 60% 45% / 45% 60% 40% 55%',
                      }}
                    >
                      👑
                    </div>
                    <div>
                      <p className="text-[#FFD700] font-black text-lg" style={{ fontFamily: 'Orbitron, sans-serif' }}>{selectedPioneer.emperor}</p>
                      <p className="text-white/40 text-xs">古代帝王类比</p>
                    </div>
                  </div>
                  <p className="text-white/65 text-sm leading-relaxed" style={{ fontFamily: 'Caveat, cursive', fontSize: '0.95rem', lineHeight: '1.8' }}>
                    {selectedPioneer.emperorDesc}
                  </p>
                </div>
              )}

              {activeTab === 'contribution' && (
                <div
                  className="p-5 rounded-2xl"
                  style={{
                    background: `${selectedPioneer.color}06`,
                    border: `1.5px solid ${selectedPioneer.color}25`,
                    borderRadius: '16px 12px 18px 14px / 12px 18px 14px 16px',
                  }}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div
                      className="w-10 h-10 flex items-center justify-center"
                      style={{
                        background: `${selectedPioneer.color}20`,
                        border: `1.5px solid ${selectedPioneer.color}50`,
                        borderRadius: '50% 40% 60% 45% / 45% 60% 40% 55%',
                      }}
                    >
                      <i className="ri-rocket-line text-lg" style={{ color: selectedPioneer.color }} />
                    </div>
                    <h4 className="text-white font-bold">对元宇宙的核心贡献</h4>
                  </div>
                  <p className="text-white/65 text-sm leading-relaxed" style={{ fontFamily: 'Caveat, cursive', fontSize: '0.95rem', lineHeight: '1.8' }}>
                    {selectedPioneer.contribution}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
